var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { AuthenticationResult, AuthenticationStatus, AuthenticationSubject } from '@mc-alberta/types';
import { State } from './state';
import { isFatalStatus } from './utils/isFatalStatus';
import { AuthenticationStrategy } from '../types/enums/AuthenticationStrategy';
import { isFidoSupported } from '../adapters/fido/utils';
import { FidoEnrollmentEligibility } from '../types/enums/FidoEnrollmentEligibility';
import { KpiState } from '../types/enums/KpiState';
import { LifecycleAwaitable, LifecycleAwaiting } from '../decorators/LifecycleAwaiter';
import { getDeserializedInstance } from './utils/getDeserializedInstance';
import { getFidoEnrollmentEligibility } from './utils/getFidoEnrollmentEligibility';
import { getIdentityVerifiedAuthorization } from './utils/getIdentityVerifiedAuthorization';
import { mergeInstanceStrategies } from './utils/mergeInstanceStrategies';
import { getAuthorization } from './utils/getAuthorization';
import { getStrategies } from './utils/getStrategies';
import { getVerificationData } from './utils/getVerificationData';
import { getAdapters } from './utils/getAdapters';
import { getInstance } from './utils/getInstance';
import { STRATEGIES, VERIFICATION_RESULT } from '../constants';
import { isInstanceMatch } from './utils/isInstanceMatch';
import { axiosService } from '../services/axios.service';
export class Stark extends State {
    /** Current account holder instance being authenticated. */
    get instance() {
        return getInstance(this.instances, this);
    }
    /** List of instances that have performed ID&V. */
    get identityVerifiedInstances() {
        return this.instances.filter(({ adapters }) => {
            return Object.values(adapters).some(({ isIdentityVerificationPerformed }) => {
                return isIdentityVerificationPerformed;
            });
        });
    }
    /** List of usable {@link strategies | `AuthenticationStrategy`}s to authenticate the active {@link instance}. */
    get strategies() {
        var _a;
        if (!this.authenticationReason)
            return [];
        return ((_a = this.instance) === null || _a === void 0 ? void 0 : _a.authenticationStrategies[this.authenticationReason]) || [];
    }
    /** Current {@link strategies | strategy} to being used to perform authentication. */
    get strategy() {
        var _a;
        return this.instance && ((_a = this.strategies) === null || _a === void 0 ? void 0 : _a[this.instance.authenticationStrategyIndex]);
    }
    set strategy(strategy) {
        if (this.instance) {
            this.instance.authenticationStrategyIndex = this.strategies.findIndex((s) => s === strategy);
        }
    }
    /** Current adapter used for the active {@link strategy}. */
    get adapter() {
        var _a;
        return this.strategy && ((_a = this.instance) === null || _a === void 0 ? void 0 : _a.adapters[this.strategy]);
    }
    /** True if there are more strategies that can be attempted for the active instance. */
    get hasMoreAttemptableStrategies() {
        var _a;
        return this.instance
            ? !!((_a = this.strategies) === null || _a === void 0 ? void 0 : _a[this.instance.authenticationStrategyIndex + 1])
            : false;
    }
    constructor(params) {
        super(params);
        this.awaiting = {};
        /** List of locales stored from `loadFidoEligibleLocales` */
        this.supportedFidoLocales = [];
        /** List of account holder instances to authenticate. */
        this.instances = [];
        /**
         * Invokes the given method name on the active session adapter.
         */
        this.invokeAdapterMethod = (methodName, ...params) => __awaiter(this, void 0, void 0, function* () {
            const session = yield (() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                const state = yield ((_a = this.adapter) === null || _a === void 0 ? void 0 : _a.getSession());
                try {
                    if (((_b = this.adapter) === null || _b === void 0 ? void 0 : _b.isAuthenticatable) &&
                        (state === null || state === void 0 ? void 0 : state.authenticationStatus) !== AuthenticationStatus.COMPLETE &&
                        !isFatalStatus(state === null || state === void 0 ? void 0 : state.authenticationStatus)) {
                        const result = yield this.adapter[methodName].apply(this.adapter, params);
                        if (isFatalStatus(result.authenticationStatus)) {
                            this.adapter.kpi.end(KpiState.FAILED);
                        }
                        else {
                            this.adapter.kpi.end(KpiState.COMPLETED, this.adapter.onKpiSuccess());
                        }
                        return result;
                    }
                }
                catch (error) {
                    (_c = this.adapter) === null || _c === void 0 ? void 0 : _c.kpi.queueError(error);
                    (_d = this.adapter) === null || _d === void 0 ? void 0 : _d.kpi.end(KpiState.FAILED);
                }
                return this.getSession();
            }))();
            this.setVerificationData(session.assuranceData);
            return session;
        });
        if (params.xhrWorker) {
            axiosService.setXhrWorker(params.xhrWorker);
        }
    }
    /**
     * Selects the `AuthenticationInstance` matching the given reference query.
     */
    useAuthenticationInstance({ authenticationSubject, authenticationReason }) {
        this.authenticationSubject = authenticationSubject;
        this.authenticationReason = authenticationReason;
        return this;
    }
    /**
     * Defines a new {@link AuthenticationInstance} in Stark.
     *
     * @param params - a {@link AuthenticationInstanceParams | full reference} to the authentication instance
     *
     * **Caution**: if an authentication instance for the given {@link AuthenticationSubject} is already
     * defined, this will throw an error.
     *
     * @see {@link removeAuthenticationInstance} to remove an authentication instance
     */
    setAuthenticationInstance(params) {
        const index = this.instances.findIndex((instance) => isInstanceMatch(instance, params));
        if (index >= 0) {
            throw new Error(`An authentication instance for ${params.authenticationSubject} is already defined.`);
        }
        // otherwise, create a new instance
        this.instances.push(Object.assign(Object.assign({}, params), { adapters: getStrategies(this.payload, params), authenticationStrategyIndex: 0 }));
    }
    /**
     * Returns the {@link AuthenticationInstance} matching the given reference query.
     */
    getAuthenticationInstance(instanceRef) {
        // do not allow `adapters` to be exported
        const _a = getInstance(this.instances, instanceRef) || {}, { adapters } = _a, rest = __rest(_a, ["adapters"]);
        return rest;
    }
    /**
     * Merges the authentication strategies in the instance parameters specified into the existing one (if any).
     */
    updateAuthenticationInstance(params) {
        const index = this.instances.findIndex((instance) => isInstanceMatch(instance, params));
        const instance = this.instances[index];
        if (!instance) {
            throw new Error('No authentication instance found for the given query.');
        }
        this.instances[index] = mergeInstanceStrategies(instance, params.authenticationStrategies);
    }
    /**
     * Removes an {@link AuthenticationInstance} from Stark.
     *
     * @returns a {@link AuthenticationInstanceParams | full reference} to the removed authentication instance, or undefined if non-existent
     *
     * **Tip**: This can be used in conjunction with {@link setAuthenticationInstance} to replace
     * an existing authentication instance with updated data.
     *
     * @example
     *  const authenticationSubject = AuthenticationSubject.CARDHOLDER
     *  const removedInstance = stark.removeAuthenticationInstance({ authenticationSubject })
     *
     *  if (removedInstance) {
     *    stark.setAuthenticationInstance({
     *      ...removedInstance,
     *      // any overrides/changes to the instance can follow...
     *      accountReference: {
     *        srcDigitalCardId: 'my-new-card-id'
     *      }
     *    })
     *    stark.loadAuthenticationMethods({ authenticationSubject })
     *    stark.preloadSessions({ authenticationSubject })
     *  }
     */
    removeAuthenticationInstance(instanceRef) {
        const instanceIndex = this.instances.findIndex((instance) => isInstanceMatch(instance, instanceRef));
        if (instanceIndex !== -1) {
            const instance = this.instances.splice(instanceIndex, 1).pop();
            return {
                accountReference: instance.accountReference,
                authenticationSubject: instance.authenticationSubject,
                authenticationStrategies: instance.authenticationStrategies
            };
        }
    }
    /**
     * @param isIdentityVerificationRequired
     * @returns {FidoEnrollmentEligibility} - NONE if FIDO is not supported
     */
    getFidoEnrollmentEligibility(isIdentityVerificationRequired = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(yield isFidoSupported()))
                return FidoEnrollmentEligibility.NONE;
            // delete the auth method from the memo and reload it due to auth provider issue
            this.lookupService.unmemoizeFidoForConsumerAuth();
            yield this.loadAuthenticationMethods();
            return getFidoEnrollmentEligibility(isIdentityVerificationRequired)(this.instances);
        });
    }
    /**
     * Calls DAS API to fetch and cache the list of eligible locales.
     */
    loadFidoEligibleLocales() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const fidoAdapter = getAdapters(this.instance).find((adapter) => {
                return adapter.strategy === AuthenticationStrategy.FIDO2 && !!adapter.accessToken;
            });
            if (((_a = this.supportedFidoLocales) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                return this.supportedFidoLocales;
            }
            if (!fidoAdapter) {
                return [];
            }
            this.supportedFidoLocales = yield fidoAdapter.getSupportedFidoLocales();
            return this.supportedFidoLocales;
        });
    }
    /**
     * Applies all authentication methods retrieved from the lookup service to their corresponding {@link Adapter}(s).
     */
    loadAuthenticationMethods() {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = this.instances.map((instance) => __awaiter(this, void 0, void 0, function* () {
                const { assuranceData, authenticationMethods } = yield this.lookup({
                    authenticationInstanceRef: instance
                });
                const adapters = Object.values(instance.adapters);
                adapters.forEach((adapter) => {
                    adapter.applyAuthenticationMethods(authenticationMethods);
                });
                if (assuranceData === null || assuranceData === void 0 ? void 0 : assuranceData.verificationData) {
                    this.setVerificationData(instance, assuranceData.verificationData);
                }
            }));
            yield Promise.all(promises);
        });
    }
    /**
     * Fetches available authentication methods from lookup service.
     */
    lookup(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const cardToken = this.getAuthorization({
                authenticationSubject: AuthenticationSubject.CARDHOLDER
            });
            const idToken = this.getAuthorization({
                authenticationSubject: AuthenticationSubject.CONSUMER
            });
            const authenticationInstance = getInstance(this.instances, params.authenticationInstanceRef);
            if (!authenticationInstance)
                return { authenticationMethods: [] };
            try {
                const { assuranceData, authenticationMethods } = yield this.lookupService.lookup({
                    authenticationInstance,
                    cardToken,
                    idToken
                });
                return { authenticationMethods, assuranceData };
            }
            catch (error) {
                return { authenticationMethods: [] };
            }
        });
    }
    /**
     * Returns a list of authentication methods for the current strategy.
     */
    getAuthenticationMethods(instanceRef, strategy) {
        var _a;
        const adapter = (_a = getInstance(this.instances, instanceRef)) === null || _a === void 0 ? void 0 : _a.adapters[strategy];
        if (!adapter)
            return [];
        return adapter.authenticationMethods;
    }
    /**
     * Returns the currently-active authentication method.
     */
    getAuthenticationMethod(instanceRef, strategy) {
        var _a;
        const adapter = (_a = getInstance(this.instances, instanceRef)) === null || _a === void 0 ? void 0 : _a.adapters[strategy];
        if (!adapter)
            return;
        return adapter.authenticationMethod;
    }
    /**
     * Pre-loads authentication sessions (where applicable) for each available strategy
     * for the given instance reference.
     */
    preloadSessions(instanceRef) {
        return __awaiter(this, void 0, void 0, function* () {
            const adapters = getAdapters(getInstance(this.instances, instanceRef));
            for (const adapter of adapters) {
                try {
                    if (adapter.isAuthenticatable) {
                        yield adapter.preloadSession();
                    }
                }
                catch (error) {
                    adapter.error = error;
                }
            }
        });
    }
    /**
     * Initializes an authentication session.
     */
    initializeSession(params) {
        var _a;
        // set the context of which we're authenticating
        this.useAuthenticationInstance(params);
        if (params.authenticationMethod) {
            // a specific method was provided - select the strategy that corresponds to it
            const { authenticationMethodType } = params.authenticationMethod;
            this.strategy = (_a = STRATEGIES.filter(({ strategy }) => {
                return this.strategies.includes(strategy);
            }).find(({ types }) => {
                return types.includes(authenticationMethodType);
            })) === null || _a === void 0 ? void 0 : _a.strategy;
        }
        return this.invokeAdapterMethod('initializeSession', params);
    }
    /**
     * Selects the next available authentication method.
     */
    selectNextUsableStrategy() {
        if (this.instance) {
            this.instance.authenticationStrategyIndex++;
        }
    }
    /**
     * Initializes the user challenge (if applicable) for the current authentication method.
     */
    provideChallenge(data) {
        return this.invokeAdapterMethod('provideChallenge', data);
    }
    /**
     * Attempts to validate the authentication session.
     */
    validateSession() {
        return this.invokeAdapterMethod('validateSession');
    }
    /**
     * Binds any ID&V performed for a cardholder to the `appInstance` (if available).
     * This is only applicable for the SRC (Click-to-Pay) program.
     *
     * @returns true if a binding was successful. False otherwise.
     */
    bindSession() {
        return __awaiter(this, void 0, void 0, function* () {
            const cardholderInstances = this.identityVerifiedInstances.filter(({ authenticationSubject }) => {
                return authenticationSubject === AuthenticationSubject.CARDHOLDER;
            });
            for (const instance of cardholderInstances) {
                for (const adapter of Object.values(instance.adapters)) {
                    if (yield adapter.bindSession()) {
                        return true;
                    }
                }
            }
            return false;
        });
    }
    /**
     * Enrolls a FIDO credential.
     *
     * @returns true if enrollment was successful. False otherwise.
     */
    enrollCredential({ locale, fidoUserName, requireIdentityVerification }) {
        return __awaiter(this, void 0, void 0, function* () {
            const instances = requireIdentityVerification ? this.identityVerifiedInstances : this.instances;
            const fidoAdapters = instances
                .concat()
                .sort((a) => {
                return a.authenticationSubject === AuthenticationSubject.CARDHOLDER ? -1 : 0;
            })
                .map(({ adapters }) => adapters[AuthenticationStrategy.FIDO2]);
            for (const adapter of fidoAdapters) {
                if (adapter.isEnrollable) {
                    return adapter.enroll(locale, fidoUserName);
                }
            }
            return false;
        });
    }
    /**
     * Binds the freshly-enrolled FIDO credential.
     *
     * @returns true if binding was successful. False otherwise.
     */
    bindCredential() {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = Object.values(this.instances)
                .map((instance) => instance.adapters[AuthenticationStrategy.FIDO2])
                .map((adapter) => adapter.bindCredential());
            const results = yield Promise.all(promises);
            return results.some((r) => r);
        });
    }
    /**
     * Caches all identity-verified tokens in all FIDO adapters.
     * These adapters will send these tokens each time it interacts with Auth Provider.
     */
    setVerifiedCredentialTokens() {
        this.instances
            .map(({ adapters }) => adapters[AuthenticationStrategy.FIDO2])
            .forEach((fidoAdapter) => {
            fidoAdapter.bindingTokens = {
                cardToken: getIdentityVerifiedAuthorization(this.identityVerifiedInstances, AuthenticationSubject.CARDHOLDER),
                idToken: getIdentityVerifiedAuthorization(this.identityVerifiedInstances, AuthenticationSubject.CONSUMER)
            };
        });
    }
    /**
     * Returns the session for the current authentication session.
     */
    getSession() {
        var _a;
        return (((_a = this.adapter) === null || _a === void 0 ? void 0 : _a.getSession()) ||
            Promise.resolve({
                authenticationResult: AuthenticationResult.NOT_AUTHENTICATED,
                authenticationStatus: AuthenticationStatus.NOT_SUPPORTED
            }));
    }
    /**
     * Resets the current authentication session back to its original state.
     */
    clearSession() {
        var _a;
        return ((_a = this.adapter) === null || _a === void 0 ? void 0 : _a.clearSession()) || this.getSession();
    }
    /**
     * Sets the given method attributes and data for the active strategy.
     */
    setMethodAttributes(payload) {
        var _a;
        (_a = this.adapter) === null || _a === void 0 ? void 0 : _a.setMethodAttributes(payload);
    }
    /**
     * Applies the given verification data onto their respective strategies.
     */
    setVerificationData(instanceRef, verificationData = []) {
        getAdapters(getInstance(this.instances, instanceRef)).forEach((adapter) => adapter.setVerificationData(verificationData));
        this.setVerifiedCredentialTokens();
    }
    /**
     * Returns a list of verifications for the requested instance, filtered by the given filter (if any).
     */
    getVerificationData(instanceRef, filter = {}) {
        return getVerificationData(getAdapters(getInstance(this.instances, instanceRef)), filter);
    }
    /**
     * Provides the corresponding authenticated token (if present) corresponding to the given instance query.
     */
    getAuthorization(instanceRef) {
        return (getAuthorization(this.getVerificationData(instanceRef, {
            verificationResults: VERIFICATION_RESULT.VERIFIED
        })) || undefined);
    }
    /**
     * Fires all pending KPI events and remote logs.
     */
    flush() {
        const adapters = this.instances.reduce((adapters, instance) => {
            return adapters.concat(getAdapters(instance));
        }, []);
        Promise.all([this.kpi.flush(), ...adapters.map((adapter) => adapter.kpi.flush())]);
    }
    /**
     * Gets the list of all pending KPI events and remote logs.
     */
    getKpiLogs() {
        const adapters = this.instances.reduce((adapters, instance) => {
            return adapters.concat(getAdapters(instance));
        }, []);
        return adapters
            .map((adapter) => adapter.kpi)
            .concat(this.kpi)
            .reduce(({ queue, remoteLogs }, kpi) => ({
            queue: queue.concat(kpi.queue),
            remoteLogs: remoteLogs.concat(kpi.remoteLogs)
        }), {
            queue: [],
            remoteLogs: []
        });
    }
    /**
     * Serializes the Stark instance.
     *
     * Note: this should do the reverse of {@link fromJSON}.
     */
    toJSON() {
        return {
            payload: this.payload,
            instances: this.instances,
            authenticationReason: this.authenticationReason,
            authenticationSubject: this.authenticationSubject,
            logs: this.getKpiLogs()
        };
    }
    /**
     * Deserializes the given payload into the current Stark instance.
     *
     * Note: this should do the reverse of {@link toJSON}.
     */
    fromJSON(parsed, payload) {
        this.kpi.queue = parsed.logs.queue;
        this.kpi.remoteLogs = parsed.logs.remoteLogs;
        this.payload = payload;
        this.instances = parsed.instances.map((instance) => {
            return getDeserializedInstance(instance, payload);
        });
        this.authenticationReason = parsed.authenticationReason;
        this.authenticationSubject = parsed.authenticationSubject;
    }
    /**
     * Returns a serialized Stark instance, or undefined if unserializable.
     */
    serialize() {
        try {
            return JSON.stringify(this);
        }
        catch (error) {
            console.error('Error serializing Stark', error);
        }
    }
    /**
     * Assigns the given state data into a new `Stark` instance.
     */
    static deserialize(data, starkInstanceParamsOverride) {
        try {
            if (!data) {
                throw new Error('Invalid input');
            }
            const parsed = JSON.parse(data);
            const starkInstanceParams = starkInstanceParamsOverride || parsed.payload;
            const stark = new Stark(starkInstanceParams);
            stark.fromJSON(parsed, starkInstanceParams);
            return stark;
        }
        catch (error) {
            console.error('Error de-serializing Stark', error);
        }
    }
}
__decorate([
    LifecycleAwaitable,
    LifecycleAwaiting('loadAuthenticationMethods'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Stark.prototype, "loadFidoEligibleLocales", null);
__decorate([
    LifecycleAwaitable,
    LifecycleAwaiting('loadAuthenticationMethods'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Stark.prototype, "loadAuthenticationMethods", null);
__decorate([
    LifecycleAwaitable,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], Stark.prototype, "lookup", null);
__decorate([
    LifecycleAwaiting('loadAuthenticationMethods'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], Stark.prototype, "preloadSessions", null);
__decorate([
    LifecycleAwaiting('loadAuthenticationMethods'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Stark.prototype, "initializeSession", null);
